import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { motion, useAnimation } from 'motion/react';
import { trackEvent, getUserTypeStatus } from '@surfline/web-common';

import CamPlayerV2 from 'components/CamPlayerV2';
import SpotThumbnailBackground from 'components/SpotThumbnailBackground';
import { useDeviceSize } from 'hooks/useDeviceSize';
import { useFramerAnimation } from 'hooks/useFramerAnimation';
import { useToggle } from 'hooks/useToggle';
import useFullscreen from 'hooks/useFullscreen';
import { useSpotIsFavorite } from 'hooks/useSpotIsFavorite';
import { getNumCams } from 'selectors/multiCam';
import createAccessibleOnClick, { BTN } from 'utils/createAccessibleOnClick';

import { AutoplayOptions, PlayerSkins } from 'components/CamPlayerV2/constants';
import { Camera } from 'types/camera';
import { SpotReportData } from 'types/spot';
import { useUserPermissionStatus } from 'selectors/user';
import { useSetCamAngleIndex } from '../hooks/useSaveMultiCamState';
import CamOverlay from './CamOverlay';
import { camLoadingVariants, camVariants } from '../variants';

import styles from '../MultiCam.module.scss';

const camClassName = (isPrimary: boolean) =>
  classNames({
    'sl-multi-cam__cam': true,
    'sl-multi-cam__cam--primary': isPrimary,
  });

const getCamContainerClass = (
  camIndex: number,
  isDragging: boolean,
  draggable: boolean,
  isFullscreen: boolean,
) =>
  classNames({
    'sl-multi-cam__cam-container': true,
    [`sl-multi-cam__cam-container--${camIndex + 1}`]: true,
    'sl-multi-cam__cam-container--dragging': isDragging,
    'sl-multi-cam__cam-container--no-drag': !draggable || isFullscreen,
    'sl-multi-cam__cam-container--draggable': draggable && !isFullscreen,
    'sl-multi-cam__cam-container--fullscreen': isFullscreen,
  });

interface Spot extends SpotReportData {
  selectedCamAngleIndex: number;
  isFiller: boolean;
}

interface Props {
  spot: Spot; // spotPropTypes,
  camIndex: number;
  primarySpotIndex: number;
  canMountCams: boolean;
  isPlaying?: boolean;
  onClick: () => void;
  camsRef: any; // PropTypes.shape({ current: PropTypes.any }),
  isPlayingAd?: boolean;
  showingPaywall?: boolean;
  isParentFullscreen: boolean | undefined;
  setIsCamFullscreen: (isFullscreen: boolean) => void;
  setLivePlaying: (livePlaying: boolean) => void;
  autoPlay?: boolean;
}

const Cam = ({
  spot,
  camIndex,
  primarySpotIndex,
  canMountCams,
  isPlaying,
  onClick,
  camsRef,
  showingPaywall,
  isParentFullscreen,
  setIsCamFullscreen,
  setLivePlaying,
  autoPlay,
}: Props) => {
  const defaultCamAngleIndex = spot?.selectedCamAngleIndex || 0;
  // State
  const [camAngle, setCamAngle] = useState({
    ...spot?.cameras?.[defaultCamAngleIndex],
    highlights: undefined,
  });
  const [camReady, setCamReady] = useToggle();
  const [isDragging, setDragging] = useState(false);
  const device = useDeviceSize();

  // Redux
  const numCams = useSelector(getNumCams);
  const userType = useSelector(getUserTypeStatus);
  const { hasPremiumCamPermissions } = useUserPermissionStatus();
  const setCamAngleIndex = useSetCamAngleIndex();

  // Custom Hooks
  const isFavorite = useSpotIsFavorite(spot?._id);

  // Constants
  const hasCam = !!spot?.cameras?.length;
  const showPremiumCamCTA = !showingPaywall && !hasPremiumCamPermissions && camAngle?.isPremium;
  const showingCam = !showingPaywall && !showPremiumCamCTA;
  const { isFiller } = spot || {};
  const draggable = camIndex === 1 && numCams === 2;
  const isPrimary = camIndex === primarySpotIndex;
  const showCamStill = hasCam && showingPaywall && !showPremiumCamCTA;

  // Cam Animations
  const camControls = useAnimation();
  const [camAnimationDone, setCamAnimationDone] = useFramerAnimation({
    controls: camControls,
    variant: 'visible',
    start: canMountCams && hasCam && camReady && showingCam,
    log: true,
  } as any);

  const showCamLoadingGradient = hasCam && !camAnimationDone && !isFiller && showingCam;

  const camRef = useRef(null);
  const [isFullscreen, setFullscreen] = useState(false);
  const [isBrowserFullScreen, toggleBrowserFullScreen] = useFullscreen(camRef);

  const toggleFullscreen = async () => {
    setFullscreen(!isFullscreen);
    setIsCamFullscreen(!isFullscreen);
    if (!isParentFullscreen && isBrowserFullScreen === isFullscreen) {
      await toggleBrowserFullScreen();
    }
  };

  useEffect(() => {
    if (!isParentFullscreen) {
      setFullscreen(false);
      setIsCamFullscreen(false);
    }
  }, [isParentFullscreen, setIsCamFullscreen]);

  // requires a different useEffect as we don't want the above one to fire
  // when we set the cam to browser fullscreen
  useEffect(() => {
    if (!isBrowserFullScreen && !isParentFullscreen) {
      setFullscreen(false);
      setIsCamFullscreen(false);
    }
  }, [isBrowserFullScreen, isParentFullscreen, setIsCamFullscreen]);

  /**
   * @description resets the cam animation state and sets the new cam angle.
   * @param {object} cam Cam object
   * @param {string} cam._id
   */
  const changeCamAngle = (cam: Camera) => {
    camControls.set('hidden');
    setCamAnimationDone(false);
    setCamReady(false);
    setCamAngle({ ...cam, highlights: undefined });
    const camAngleIndex = spot?.cameras.findIndex((camera: any) => camera._id === cam._id);
    setCamAngleIndex(camIndex, camAngleIndex);
    trackEvent('Toggled Cam View', {
      category: 'cams & reports',
      name: 'multi-cam - angle',
      subregionId: spot?.subregionId,
      spotId: spot?._id,
      camId: cam?._id,
      camName: cam?.title,
      isMobileView: device.width < 976,
      view: numCams > 1 ? 'MULTI' : 'SINGLE',
      camNumber: numCams,
      subscriptionEntitlement: userType,
    });
  };

  return (
    <motion.div
      drag={draggable}
      dragConstraints={camsRef}
      dragElastic={0.3}
      dragMomentum={false}
      onDragStart={() => setDragging(true)}
      onDragEnd={() => setDragging(false)}
      className={getCamContainerClass(
        camIndex,
        isDragging,
        draggable,
        isFullscreen || isBrowserFullScreen,
      )}
    >
      <div className={camClassName(isPrimary)} ref={camRef}>
        {!isPrimary && (
          <div
            aria-label={spot?.name ? `Select ${spot.name} camera` : 'Select camera'}
            className={classNames(styles.camOverlayButton, 'sl-multi-cam__click-overlay')}
            {...createAccessibleOnClick(() => onClick(), BTN)}
          />
        )}
        {showCamLoadingGradient && (
          <motion.div
            className="sl-multi-cam__loading-gradient"
            animate="animate"
            initial="initial"
            variants={camLoadingVariants}
          />
        )}
        {!hasCam && <SpotThumbnailBackground spot={spot} />}
        {hasCam && showingCam && (
          <motion.div
            className="sl-multi-cam__cams_cam"
            variants={camVariants}
            initial="hidden"
            animate={camControls}
          >
            <CamPlayerV2
              autoplay={
                autoPlay ? AutoplayOptions.AUTOPLAY_ENABLED : AutoplayOptions.AUTOPLAY_DISABLED
              }
              camReady={camReady}
              camera={camAngle}
              controlledPlayback
              handleCamReady
              isFavorite={isFavorite}
              isMultiCam
              isPlaying={isPlaying}
              key={`${camAngle?._id}${camIndex}`}
              location="Multi Cam"
              playerId={`${camAngle?._id}${camIndex}`}
              playerSkin={PlayerSkins.NONE}
              setCamReady={setCamReady}
              spotId={spot?._id}
              spotName={spot?.name}
              onToggleStreaming={setLivePlaying}
            />
          </motion.div>
        )}
        {showCamStill && (
          <div className="sl-cam-still" style={{ backgroundImage: `url(${camAngle?.stillUrl})` }} />
        )}
        <CamOverlay
          changeCamAngle={changeCamAngle}
          camAngle={camAngle}
          camIndex={camIndex}
          spot={spot || {}}
          isFullscreen={!!isFullscreen || !!isBrowserFullScreen}
          toggleFullscreen={toggleFullscreen}
          showingPremiumCamCTA={showPremiumCamCTA}
        />
      </div>
    </motion.div>
  );
};

export default React.memo(Cam);
